import React, { useState, useEffect, useRef } from 'react';
import { getXautoMonitorCountsByClients } from '../../../Redux/qms/action';
import { HiCpuChip } from 'react-icons/hi2';
import { LuFileInput, LuHourglass } from 'react-icons/lu';
import { BiBuildings, BiCheckCircle } from 'react-icons/bi';
import { TbInbox } from 'react-icons/tb';
import './SummarizedViewTable.css';

const PAGE_SIZE = 24;

const METRICS = [
    { key: 'automation_pending', label: 'Automation Pending', icon: LuHourglass, tone: 'pending' },
    { key: 'queued_for_processing', label: 'Queued for Processing', icon: LuFileInput, tone: 'queued' },
    { key: 'automation_inprocess', label: 'Automation In Process', icon: HiCpuChip, tone: 'inprocess' },
];

const SkeletonCard = () => (
    <div className="smv-card smv-skeleton">
        <div className="smv-skel-line w-50" />
        <div className="smv-skel-line w-25" />
        <div className="smv-skel-metrics">
            <div /><div /><div />
        </div>
    </div>
);

const SummarizedViewTable = ({partnerFilter, clientFilter, searchTrigger, onCardClick }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [exhausted, setExhausted] = useState(false);

    const sentinelRef = useRef(null);
    const requestIdRef = useRef(0);

    const hasMore = !exhausted && data.length < totalRows;

    const fetchSummarizedData = (currentPage = 1) => {
        const isFirstPage = currentPage === 1;
        const requestId = ++requestIdRef.current;
        isFirstPage ? setLoading(true) : setLoadingMore(true);

        const params = {
            page: currentPage,
            limit: PAGE_SIZE,
            clients: Array.isArray(clientFilter) ? clientFilter?.join(',') : '',
            partners: Array.isArray(partnerFilter) ? partnerFilter?.join(',') : '',
            sort_key: '',
            sort_order: 'asc'
        };

        getXautoMonitorCountsByClients(params)((response) => {
            // Ignore responses from a superseded search
            if (requestId !== requestIdRef.current) return;

            let tableData = [];
            let count = 0;

            if (response) {
                if (response.status === true || response.status === "true") {
                    const nestedData = response.data;
                    if (nestedData) {
                        tableData = nestedData.results?.data || nestedData.results || (Array.isArray(nestedData) ? nestedData : []);
                        count = nestedData.count !== undefined ? nestedData.count : tableData.length;
                    }
                } else {
                    tableData = response.results || (Array.isArray(response) ? response : []);
                    count = response.count !== undefined ? response.count : tableData.length;
                }
            }

            setData(prev => isFirstPage ? tableData : [...prev, ...tableData]);
            setTotalRows(count);
            setExhausted(tableData.length === 0);
            setPage(currentPage);
            setLoading(false);
            setLoadingMore(false);
        });
    };

    useEffect(() => {
        setData([]);
        fetchSummarizedData(1);
    }, [searchTrigger]);

    // Load the next page when the bottom sentinel scrolls into view
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || loading || loadingMore || !hasMore) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                fetchSummarizedData(page + 1);
            }
        }, { rootMargin: '200px' });

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loading, loadingMore, hasMore, page]);

    return (
        <div className="summarized-view-container smv">
            {!loading && data.length > 0 && (
                <div className="smv-header">
                    <span className="smv-count-pill">
                        Showing <strong>{data.length}</strong> of <strong>{totalRows}</strong> clients
                    </span>
                </div>
            )}

            {loading ? (
                <div className="smv-grid">
                    {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
                </div>
            ) : data.length === 0 ? (
                <div className="smv-empty">
                    <TbInbox className="smv-empty-icon" />
                    <div className="smv-empty-title">No records found</div>
                    <div className="smv-empty-text">Try changing the partner or client filter.</div>
                </div>
            ) : (
                <>
                    <div className="smv-grid">
                        {data.map((row, index) => {
                            const rowTotal = METRICS.reduce((sum, m) => sum + (Number(row?.[m.key]) || 0), 0);
                            return (
                                <div
                                    key={`${row.partner}-${row.client}-${index}`}
                                    className="smv-card smv-card-enter smv-clickable"
                                    style={{ animationDelay: `${(index % PAGE_SIZE) * 25}ms` }}
                                    role="button"
                                    tabIndex={0}
                                    title="View all jobs in detailed view"
                                    onClick={() => onCardClick?.(row, METRICS.map(m => m.key))}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onCardClick?.(row, METRICS.map(m => m.key));
                                        }
                                    }}
                                >
                                    <div className="smv-card-head">
                                        <div className="smv-card-title-wrap">
                                            <span className="smv-avatar">{(row.client || '?').charAt(0).toUpperCase()}</span>
                                            <div className="smv-card-titles">
                                                <div className="smv-card-title" title={row.client}>{row.client || '-'}</div>
                                                <div className="smv-card-subtitle" title={row.partner}>
                                                    <BiBuildings /> {row.partner || '-'}
                                                </div>
                                            </div>
                                        </div>
                                        <span className="smv-total-chip" title="Total jobs">{rowTotal}</span>
                                    </div>

                                    <div className="smv-metrics">
                                        {METRICS.map(({ key, label, icon: Icon, tone }) => (
                                            <div
                                                key={key}
                                                className={`smv-metric smv-tone-${tone}`}
                                                title={`View ${label} jobs`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onCardClick?.(row, [key]);
                                                }}
                                            >
                                                <div className="smv-metric-top">
                                                    <Icon className="smv-metric-icon" />
                                                    <span className="smv-metric-value">{row?.[key] ?? 0}</span>
                                                </div>
                                                <div className="smv-metric-label">{label}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="smv-bar" title={`Total: ${rowTotal}`}>
                                        {rowTotal > 0 && METRICS.map(({ key, tone }) => {
                                            const value = Number(row?.[key]) || 0;
                                            return value > 0 ? (
                                                <span key={key} className={`smv-bar-seg smv-tone-${tone}`} style={{ width: `${(value / rowTotal) * 100}%` }} />
                                            ) : null;
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                        {loadingMore && Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={`more-${i}`} />)}
                    </div>

                    <div ref={sentinelRef} className="smv-sentinel" />

                    {!hasMore && data.length > PAGE_SIZE && (
                        <div className="smv-end">
                            <BiCheckCircle /> All {data.length} clients loaded
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SummarizedViewTable;
